import { client } from "../api/client.js";
import { toast } from '../ui/toast.js';

const ROLE_ORDER = { god_admin: 0, angel_admin: 1, admin: 2, user: 3 };

export class AdminPage {
    constructor() {
        this.el = null;
        this.stats = null;
        this.storage = null;
        this.users = [];
        this.discountCodes = [];
        this.currentUserRole = null;
        this.currentTab = 'dashboard';
        // Dashboard-subtab state
        this.dashboardSummary = null;
        this.dashboardEvents = [];
        this._dashboardInterval = null;
        this.grantFreeUserId = null;
        this.searchQuery = '';
        this.filterTier = '';
        this.filterRole = '';
        this.filterBilling = '';
        this.filterStatus = '';
        this.sortCol = 'created_at';
        this.sortDir = 'desc';
        this.pageSize = 25;
        this.currentPage = 1;
        this.selectedIds = new Set();
        this.showPasswordModal = false;
        this.pendingPermDeleteIds = [];
        // Workspace management
        this.workspaces = [];
        this.totalWorkspaces = 0;
        this.wsCurrentPage = 1;
        this.wsPageSize = 25;
        this.wsSearch = '';
        this.expandedWorkspaceId = null;
        this.expandedWorkspaceData = null;
        this.showCreateWorkspace = false;
        // User feedback / support tickets
        this.feedbackItems = [];
        this.feedbackCounts = null;
        this.feedbackFilter = 'open';
    }

    async mount(el) {
        this.el = el;
        this.el.innerHTML = '<div class="admin-page"><div class="admin-loading">Loading admin dashboard...</div></div>';
        // Deep link: #admin?tab=feedback (used by the new-feedback admin notification)
        const tabMatch = window.location.hash.match(/[?&]tab=(\w+)/);
        if (tabMatch && ['dashboard', 'active', 'archived', 'deleted', 'workspaces', 'feedback'].includes(tabMatch[1])) {
            this.currentTab = tabMatch[1];
        }
        try {
            const meRes = await client.authMe();
            this.currentUserRole = meRes.user?.role;
            // Block admin panel while impersonating another user
            if (meRes.user?.impersonating) {
                this.el.innerHTML = '<div class="admin-page"><div class="admin-error">Admin panel is not available while impersonating another user. Use the banner above to return to your account first.</div></div>';
                return;
            }
        } catch (e) {
            this.currentUserRole = 'admin';
        }
        await this.loadData();
        if (this.currentTab === 'feedback') await this.loadFeedback();
        this.render();
        // Auto-refresh dashboard summary every 60s while the user is viewing it.
        this._dashboardInterval = setInterval(() => {
            if (this.currentTab !== 'dashboard' || !this.el) return;
            this.loadDashboard().then(() => this.render()).catch(() => {});
        }, 60 * 1000);
    }

    get isGodAdmin() { return this.currentUserRole === 'god_admin'; }
    get isAngelAdmin() { return this.currentUserRole === 'angel_admin'; }
    get isGodOrAngel() { return this.isGodAdmin || this.isAngelAdmin; }

    async loadData() {
        try {
            // The users-table query uses a server tab name ('active', 'archived', 'deleted').
            // When the Dashboard subtab is active, query the 'active' list so top-level
            // stats (totalUsers, etc.) stay populated for both Dashboard cards and a
            // later tab switch.
            const usersTab = (this.currentTab === 'dashboard' || this.currentTab === 'feedback') ? 'active' : this.currentTab;
            const [statsRes, usersRes, discountRes] = await Promise.all([
                client.adminGetStats(),
                client.adminGetUsers({ tab: usersTab, page: this.currentPage, limit: this.pageSize, search: this.searchQuery || '', tier: this.filterTier || '', role: this.filterRole || '', billing: this.filterBilling || '', status: this.filterStatus || '', sort: this.sortCol, dir: this.sortDir }),
                client.discountList()
            ]);
            this.stats = statsRes.stats;
            this.users = usersRes.users || [];
            this.totalUsers = usersRes.total || this.users.length;
            this.discountCodes = discountRes.codes || [];
            // Storage stats are god_admin only; load separately and swallow errors
            // so non-god admins still see the rest of the dashboard.
            client.adminGetStorage().then((res) => {
                if (res?.ok) { this.storage = res.storage; this.render(); }
            }).catch(() => {});
            // Dashboard summary + events (cheap; keep fresh on every load)
            await this.loadDashboard();
            // BE-10 — load the publish dead-letter queue summary in the background.
            // Same pattern: don't block the rest of the dashboard if it fails.
            client.adminGetDeadLetter().then((res) => {
                if (res?.ok) { this.dlq = res; this.render(); }
            }).catch(() => {});
            // Phase 35 — integrations needing reconnect (token expired).
            client.adminGetIntegrationsHealth().then((res) => {
                if (res?.ok) { this.integHealth = res; this.render(); }
            }).catch(() => {});
            // R6 Stage 3 — background-job status (Ops). Same non-blocking pattern.
            client.adminGetOpsJobs().then((res) => {
                if (res?.ok) { this.opsJobs = res; this.render(); }
            }).catch(() => {});
        } catch (err) {
            console.error('[ADMIN] Load error:', err);
            this.el.innerHTML = '<div class="admin-page"><div class="admin-error">Failed to load admin data. You may not have admin access.</div></div>';
        }
    }

    async loadDashboard() {
        try {
            const [sum, ev, funnel] = await Promise.all([
                client.adminGetDashboardSummary(),
                client.adminGetDashboardEvents(50),
                client.adminGetFunnel(12).catch(() => null),
            ]);
            this.dashboardSummary = sum;
            this.dashboardEvents = ev.events || [];
            this.funnelData = funnel;
        } catch (err) {
            console.warn('[ADMIN_DASHBOARD] load failed:', err.message);
        }
    }

    // T-04 — weekly signup cohorts × milestones, from /api/admin/funnel
    renderFunnelTable() {
        const f = this.funnelData;
        if (!f || !Array.isArray(f.cohorts)) return '';
        const COLS = [
            ['signups', 'Signups'], ['verified', 'Verified'], ['setup_completed', 'Setup'],
            ['mcp_connected', 'MCP'], ['first_draft', '1st draft'], ['first_schedule', '1st sched'],
            ['first_publish', '1st publish'], ['channel_connected', 'Channel'], ['ai_key', 'AI key'],
            ['paywall_viewed', 'Paywall'], ['checkout_started', 'Checkout'], ['subscribed', 'Paid'],
        ];
        const pct = (n, base) => base > 0 ? `<span class="admin-funnel-pct">${Math.round((n / base) * 100)}%</span>` : '';
        const rows = f.cohorts.map(c => `
            <tr>
                <td style="white-space:nowrap">${c.week_start}</td>
                ${COLS.map(([k]) => k === 'signups'
                    ? `<td><strong>${c.signups}</strong></td>`
                    : `<td>${c[k] || 0} ${pct(c[k] || 0, c.signups)}</td>`).join('')}
                <td>${c.median_min_to_first_draft == null ? '—' : c.median_min_to_first_draft + 'm'}</td>
                <td>${c.median_min_to_first_schedule == null ? '—' : c.median_min_to_first_schedule + 'm'}</td>
            </tr>`).join('');
        return `
            <div class="admin-dashboard__funnel">
                <div class="admin-dashboard__section-head">
                    <h3>Cohort funnel <span class="admin-dash-count">last ${f.weeks} weeks · % of cohort signups</span></h3>
                </div>
                ${f.cohorts.length === 0 ? '<div class="admin-dash-event admin-dash-event--empty">No signups in the window.</div>' : `
                <div style="overflow-x:auto">
                <table class="admin-table admin-funnel-table">
                    <thead><tr><th>Week</th>${COLS.map(([, label]) => `<th>${label}</th>`).join('')}<th>Med→draft</th><th>Med→sched</th></tr></thead>
                    <tbody>${rows}</tbody>
                </table>
                </div>`}
            </div>`;
    }

    formatBytes(bytes) {
        if (bytes == null || isNaN(bytes)) return '—';
        if (bytes === 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        const v = bytes / Math.pow(1024, i);
        return `${v.toFixed(v < 10 && i > 0 ? 2 : v < 100 ? 1 : 0)} ${units[i]}`;
    }

    renderStoragePanel() {
        if (!this.storage) return '';
        const s = this.storage;
        const fmt = (b) => this.formatBytes(b);
        const diskRow = s.disk ? `
            <div class="admin-storage-disk">
                <div class="admin-storage-disk-label">
                    <span>Disk (${s.hostname})</span>
                    <span>${fmt(s.disk.usedBytes)} / ${fmt(s.disk.totalBytes)} used · ${fmt(s.disk.freeBytes)} free</span>
                </div>
                <div class="admin-storage-disk-bar">
                    <div class="admin-storage-disk-fill ${s.disk.usedPct > 0.85 ? 'admin-storage-disk-fill--danger' : s.disk.usedPct > 0.70 ? 'admin-storage-disk-fill--warn' : ''}" style="width: ${Math.round(s.disk.usedPct * 100)}%"></div>
                </div>
                <div class="admin-storage-disk-pct">${Math.round(s.disk.usedPct * 100)}% full</div>
            </div>
        ` : `<div class="admin-storage-disk-na">Disk-level stats not available on this platform (${s.platform})</div>`;
        return `
            <div class="admin-section admin-storage-section" style="margin-top:24px;">
                <div class="admin-section-header">
                    <h2>Platform Storage</h2>
                    <span class="admin-section-sub">Track capacity before you run out. Updated ${new Date(s.generatedAt).toLocaleTimeString()}</span>
                </div>
                ${diskRow}
                <div class="admin-storage-grid">
                    <div class="admin-storage-card">
                        <div class="admin-storage-card-label">Database</div>
                        <div class="admin-storage-card-value">${fmt(s.database?.bytes)}</div>
                        <div class="admin-storage-card-hint">SQLite (contentpro.db)</div>
                    </div>
                    <div class="admin-storage-card">
                        <div class="admin-storage-card-label">Uploads</div>
                        <div class="admin-storage-card-value">${fmt(s.directories?.uploads?.bytes)}</div>
                        <div class="admin-storage-card-hint">${s.directories?.uploads?.files || 0} files</div>
                    </div>
                    <div class="admin-storage-card">
                        <div class="admin-storage-card-label">Data directory</div>
                        <div class="admin-storage-card-value">${fmt(s.directories?.data?.bytes)}</div>
                        <div class="admin-storage-card-hint">${s.directories?.data?.files || 0} files (incl. backups)</div>
                    </div>
                    <div class="admin-storage-card">
                        <div class="admin-storage-card-label">Logs</div>
                        <div class="admin-storage-card-value">${fmt(s.directories?.logs?.bytes)}</div>
                        <div class="admin-storage-card-hint">${s.directories?.logs?.files || 0} files</div>
                    </div>
                    <div class="admin-storage-card">
                        <div class="admin-storage-card-label">Workspace media (DB)</div>
                        <div class="admin-storage-card-value">${fmt(s.workspaceMediaBytes)}</div>
                        <div class="admin-storage-card-hint">tracked in workspace_media</div>
                    </div>
                    <div class="admin-storage-card admin-storage-card--total">
                        <div class="admin-storage-card-label">App footprint</div>
                        <div class="admin-storage-card-value">${fmt(s.trackedBytes)}</div>
                        <div class="admin-storage-card-hint">data + uploads + logs</div>
                    </div>
                </div>
                ${s.byUser?.length ? `
                    <div class="admin-storage-users">
                        <div class="admin-storage-users-title">Top users by post + document count</div>
                        <table class="admin-table admin-storage-table">
                            <thead><tr><th>User</th><th>Tier</th><th>Posts</th><th>Documents</th></tr></thead>
                            <tbody>
                                ${s.byUser.slice(0, 10).map((u) => `
                                    <tr>
                                        <td>${this.esc(u.email)}</td>
                                        <td><span class="admin-tier-pill admin-tier-pill--${u.tier || 'trial'}">${u.tier || 'trial'}</span></td>
                                        <td>${u.post_count}</td>
                                        <td>${u.document_count}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                ` : ''}
            </div>
        `;
    }

    async loadWorkspaces() {
        try {
            const res = await client.adminGetWorkspaces({ page: this.wsCurrentPage, limit: this.wsPageSize, search: this.wsSearch || '' });
            this.workspaces = res.workspaces || [];
            this.totalWorkspaces = res.total || 0;
        } catch (err) {
            console.error('[ADMIN] Load workspaces error:', err);
        }
    }

    async loadFeedback() {
        try {
            const res = await client.adminListFeedback(this.feedbackFilter ? { status: this.feedbackFilter } : {});
            this.feedbackItems = res.items || [];
            this.feedbackCounts = res.counts || null;
        } catch (err) {
            console.error('[ADMIN] Load feedback error:', err);
            toast('error', 'Failed to load feedback');
        }
    }

    async loadWorkspaceDetail(id) {
        try {
            const res = await client.adminGetWorkspace(id);
            this.expandedWorkspaceData = res;
            this.expandedWorkspaceId = id;
        } catch (err) {
            console.error('[ADMIN] Load workspace detail error:', err);
            toast('error', 'Failed to load workspace details');
        }
    }

    getSortedFilteredUsers() {
        let list = [...this.users];
        // Search filter
        if (this.searchQuery) {
            const q = this.searchQuery.toLowerCase();
            list = list.filter(u =>
                (u.email || '').toLowerCase().includes(q) ||
                (u.name || '').toLowerCase().includes(q) ||
                (u.role || '').toLowerCase().includes(q) ||
                (u.tier || '').toLowerCase().includes(q)
            );
        }
        // Sort: role priority first, then oldest signup first
        list.sort((a, b) => {
            const ra = ROLE_ORDER[a.role] ?? 99;
            const rb = ROLE_ORDER[b.role] ?? 99;
            if (ra !== rb) return ra - rb;
            const da = new Date(a.created_at || 0).getTime();
            const db = new Date(b.created_at || 0).getTime();
            return da - db;
        });
        return list;
    }

    getPagedUsers(sorted) {
        const start = (this.currentPage - 1) * this.pageSize;
        return sorted.slice(start, start + this.pageSize);
    }

    renderBulkBar(paged) {
        const count = this.selectedIds.size;
        if (count === 0) return '';

        const isDeletedTab = this.currentTab === 'deleted';
        const isActiveTab = this.currentTab === 'active';
        const isArchivedTab = this.currentTab === 'archived';

        let actions = '';
        if (isActiveTab) {
            actions += `<button class="admin-bulk-btn" data-bulk-action="archived">Archive Selected</button>`;
            actions += `<button class="admin-bulk-btn" data-bulk-action="suspended">Suspend Selected</button>`;
            if (this.isGodOrAngel) {
                actions += `<button class="admin-bulk-btn admin-bulk-btn--danger" data-bulk-action="deleted">Delete Selected</button>`;
            }
        }
        if (isArchivedTab) {
            actions += `<button class="admin-bulk-btn" data-bulk-action="active">Restore Selected</button>`;
            if (this.isGodOrAngel) {
                actions += `<button class="admin-bulk-btn admin-bulk-btn--danger" data-bulk-action="deleted">Delete Selected</button>`;
            }
        }
        if (isDeletedTab) {
            actions += `<button class="admin-bulk-btn" data-bulk-action="active">Restore Selected</button>`;
            if (this.isGodAdmin) {
                actions += `<button class="admin-bulk-btn admin-bulk-btn--danger" id="admin-bulk-perm-delete">Permanently Delete (${count})</button>`;
            }
        }

        return `
        <div class="admin-bulk-bar">
            <span class="admin-bulk-count">${count} user${count !== 1 ? 's' : ''} selected</span>
            <div class="admin-bulk-actions">${actions}</div>
            <button class="admin-bulk-btn admin-bulk-btn--clear" id="admin-bulk-clear">Clear Selection</button>
        </div>`;
    }

    renderPasswordModal() {
        if (!this.showPasswordModal) return '';
        const count = this.pendingPermDeleteIds.length;
        return `
        <div class="admin-modal-overlay" id="admin-modal-overlay">
            <div class="admin-modal">
                <div class="admin-modal-header">
                    <h3>Confirm Permanent Deletion</h3>
                </div>
                <div class="admin-modal-body">
                    <p>You are about to <strong>permanently delete ${count} user${count !== 1 ? 's' : ''}</strong> and all their data. This action <strong>cannot be undone</strong>.</p>
                    <p>Enter your password to confirm:</p>
                    <input type="password" id="admin-perm-delete-password" class="admin-modal-input" placeholder="Your password" autocomplete="off" />
                    <div class="admin-modal-error" id="admin-modal-error" style="display:none;"></div>
                </div>
                <div class="admin-modal-footer">
                    <button class="admin-action-btn" id="admin-modal-cancel">Cancel</button>
                    <button class="admin-action-btn admin-action-btn--danger" id="admin-modal-confirm">Permanently Delete</button>
                </div>
            </div>
        </div>`;
    }

    render() {
        if (!this.stats) return;
        const s = this.stats;
        const sorted = this.getSortedFilteredUsers();
        const totalPages = Math.max(1, Math.ceil((this.totalUsers || sorted.length) / this.pageSize));
        if (this.currentPage > totalPages) this.currentPage = totalPages;
        const paged = this.users;

        // Clean up selectedIds — remove any that aren't in the current user list
        const currentUserIds = new Set(this.users.map(u => u.id));
        for (const id of this.selectedIds) {
            if (!currentUserIds.has(id)) this.selectedIds.delete(id);
        }

        // Check if all visible (non-god_admin) rows are selected
        const selectableIds = paged.filter(u => u.role !== 'god_admin').map(u => u.id);
        const allPageSelected = selectableIds.length > 0 && selectableIds.every(id => this.selectedIds.has(id));

        this.el.innerHTML = `
        <div class="admin-page">
            <div class="admin-header">
                <h1>Admin Dashboard</h1>
            </div>

            ${this.currentTab === 'dashboard' ? '' : `
            <div class="admin-stats-grid">
                ${this.currentTab === 'active' ? `
                <div class="admin-stat-card"><div class="admin-stat-value">${s.totalUsers}</div><div class="admin-stat-label">Active Users</div></div>
                <div class="admin-stat-card"><div class="admin-stat-value">${s.totalPosts}</div><div class="admin-stat-label">Total Posts</div></div>
                <div class="admin-stat-card"><div class="admin-stat-value">${s.totalCampaigns}</div><div class="admin-stat-label">Campaigns</div></div>
                <div class="admin-stat-card"><div class="admin-stat-value">${s.totalScheduled}</div><div class="admin-stat-label">Scheduled</div></div>
                <div class="admin-stat-card"><div class="admin-stat-value">${s.activeThisWeek || 0}</div><div class="admin-stat-label">Active This Week</div></div>
                <div class="admin-stat-card"><div class="admin-stat-value">${s.signupsToday || 0}</div><div class="admin-stat-label">Signups Today</div></div>
                <div class="admin-stat-card"><div class="admin-stat-value">${s.trialUsers || 0}</div><div class="admin-stat-label">On Trial</div></div>
                <div class="admin-stat-card"><div class="admin-stat-value">${s.expiredTrials || 0}</div><div class="admin-stat-label">Expired Trials</div></div>
                ` : this.currentTab === 'archived' ? `
                <div class="admin-stat-card"><div class="admin-stat-value">${s.archivedUsers || 0}</div><div class="admin-stat-label">Archived Users</div></div>
                ` : (this.currentTab === 'workspaces' || this.currentTab === 'feedback') ? `` : `
                <div class="admin-stat-card"><div class="admin-stat-value">${s.deletedUsers || 0}</div><div class="admin-stat-label">Deleted Users</div></div>
                `}
            </div>
            `}

            <div class="admin-tabs">
                <button class="admin-tab ${this.currentTab === 'dashboard' ? 'active' : ''}" data-tab="dashboard">Dashboard</button>
                <button class="admin-tab ${this.currentTab === 'active' ? 'active' : ''}" data-tab="active">Active Users</button>
                <button class="admin-tab ${this.currentTab === 'archived' ? 'active' : ''}" data-tab="archived">Archived</button>
                ${this.isGodOrAngel ? `<button class="admin-tab ${this.currentTab === 'deleted' ? 'active' : ''}" data-tab="deleted">Deleted</button>` : ''}
                ${this.isGodOrAngel ? `<button class="admin-tab ${this.currentTab === 'workspaces' ? 'active' : ''}" data-tab="workspaces">Workspaces</button>` : ''}
                <button class="admin-tab ${this.currentTab === 'feedback' ? 'active' : ''}" data-tab="feedback" data-testid="admin-tab-feedback">Feedback${this.feedbackCounts && this.feedbackCounts.open > 0 ? ` (${this.feedbackCounts.open})` : ''}</button>
            </div>

            ${this.currentTab === 'dashboard' ? this.renderDashboardTab() : this.currentTab === 'workspaces' ? this.renderWorkspacesTab() : this.currentTab === 'feedback' ? this.renderFeedbackTab() : `
            <div class="admin-toolbar">
                <div class="admin-search-wrap">
                    <input type="text" id="admin-search" class="admin-search-input" placeholder="Search users..." value="${this.esc(this.searchQuery)}" />
                </div>
                <select id="admin-filter-role" class="admin-filter-select" title="Filter by role">
                    <option value="">All Roles</option>
                    <option value="god_admin" ${this.filterRole === 'god_admin' ? 'selected' : ''}>God Admin</option>
                    <option value="angel_admin" ${this.filterRole === 'angel_admin' ? 'selected' : ''}>Angel Admin</option>
                    <option value="admin" ${this.filterRole === 'admin' ? 'selected' : ''}>Admin</option>
                    <option value="user" ${this.filterRole === 'user' ? 'selected' : ''}>User</option>
                </select>
                <select id="admin-filter-tier" class="admin-filter-select" title="Filter by tier">
                    <option value="">All Tiers</option>
                    <option value="trial" ${this.filterTier === 'trial' ? 'selected' : ''}>Trial</option>
                    <option value="starter" ${this.filterTier === 'starter' ? 'selected' : ''}>Starter</option>
                    <option value="pro" ${this.filterTier === 'pro' ? 'selected' : ''}>Pro</option>
                    <option value="founder" ${this.filterTier === 'founder' ? 'selected' : ''}>Founder</option>
                    <option value="agency" ${this.filterTier === 'agency' ? 'selected' : ''}>Agency</option>
                </select>
                <select id="admin-filter-billing" class="admin-filter-select" title="Filter by billing">
                    <option value="">All Billing</option>
                    <option value="monthly" ${this.filterBilling === 'monthly' ? 'selected' : ''}>Monthly</option>
                    <option value="annual" ${this.filterBilling === 'annual' ? 'selected' : ''}>Annual</option>
                </select>
                <select id="admin-filter-status" class="admin-filter-select" title="Filter by status">
                    <option value="">All Status</option>
                    <option value="active" ${this.filterStatus === 'active' ? 'selected' : ''}>Active</option>
                    <option value="inactive" ${this.filterStatus === 'inactive' ? 'selected' : ''}>Inactive</option>
                    <option value="suspended" ${this.filterStatus === 'suspended' ? 'selected' : ''}>Suspended</option>
                </select>
                <div class="admin-page-size-wrap">
                    <label>Show</label>
                    <select id="admin-page-size" class="admin-page-size-select">
                        ${[10, 25, 50, 100].map(n => `<option value="${n}" ${this.pageSize === n ? 'selected' : ''}>${n}</option>`).join('')}
                    </select>
                    <span class="admin-result-count">${sorted.length} user${sorted.length !== 1 ? 's' : ''}</span>
                </div>
            </div>

            ${this.renderBulkBar(paged)}

            <div class="admin-section">
                <div class="admin-table-wrap">
                    <table class="admin-table" data-testid="admin-users-table">
                        <thead>
                            <tr>
                                <th style="width:30px;text-align:center;">
                                    <input type="checkbox" class="admin-select-all" ${allPageSelected ? 'checked' : ''} title="Select / deselect all" />
                                </th>
                                <th style="width:22px"></th>
                                <th style="width:20%;cursor:pointer" data-sort="email">Email ${this.sortCol === 'email' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:10%;cursor:pointer" data-sort="name">Name ${this.sortCol === 'name' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:9%;cursor:pointer" data-sort="role">Role ${this.sortCol === 'role' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:8%;cursor:pointer" data-sort="tier">Tier ${this.sortCol === 'tier' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:9%">Billing</th>
                                <th style="width:8%">Status</th>
                                <th style="width:40px;cursor:pointer" data-sort="post_count">Posts ${this.sortCol === 'post_count' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:8%;cursor:pointer" data-sort="signup_source">Source ${this.sortCol === 'signup_source' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:7%;cursor:pointer" data-sort="email_verified">Verified ${this.sortCol === 'email_verified' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:8%;cursor:pointer" data-sort="last_login_at">Last Login ${this.sortCol === 'last_login_at' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th style="width:8%;cursor:pointer" data-sort="created_at">Joined ${this.sortCol === 'created_at' ? (this.sortDir === 'asc' ? '&#9650;' : '&#9660;') : ''}</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${paged.map(u => this.renderUserRow(u)).join('')}
                            ${paged.length === 0 ? `<tr><td colspan="14" style="text-align:center;padding:32px;color:var(--text-muted);">No users found</td></tr>` : ''}
                        </tbody>
                    </table>
                </div>
                ${totalPages > 1 ? `
                <div class="admin-pagination">
                    <button class="admin-page-btn" data-page="${this.currentPage - 1}" ${this.currentPage <= 1 ? 'disabled' : ''}>← Prev</button>
                    <span class="admin-page-info">Page ${this.currentPage} of ${totalPages}</span>
                    <button class="admin-page-btn" data-page="${this.currentPage + 1}" ${this.currentPage >= totalPages ? 'disabled' : ''}>Next →</button>
                </div>` : ''}
            </div>

            <div class="admin-section">
                <h2>Discount Codes</h2>
                <div class="admin-discount-form">
                    <input type="text" id="admin-dc-code" placeholder="CODE" class="input" />
                    <select id="admin-dc-type" class="input"><option value="percent">Percent</option><option value="fixed">Fixed</option></select>
                    <input type="number" id="admin-dc-amount" placeholder="Amount" class="input" step="1" />
                    <input type="number" id="admin-dc-max" placeholder="Max uses" class="input" />
                    <select id="admin-dc-tier" class="input" title="Tier granted on 100% off">
                        <option value="pro">Grants: Pro</option>
                        <option value="starter">Grants: Starter</option>
                        <option value="founder">Grants: Founder</option>
                        <option value="agency">Grants: Agency</option>
                    </select>
                    <input type="number" id="admin-dc-days" placeholder="Duration (days)" class="input" value="30" title="Days of access (blank=permanent)" />
                    <button id="admin-dc-create" class="btn-primary sm">Create Code</button>
                </div>
                <div class="admin-table-wrap">
                    <table class="admin-table">
                        <thead><tr><th>Code</th><th>Type</th><th>Amount</th><th>Uses</th><th>Grants</th><th>Active</th><th>Actions</th></tr></thead>
                        <tbody>
                            ${this.discountCodes.map(dc => `
                            <tr>
                                <td><code>${this.esc(dc.code)}</code></td>
                                <td>${dc.type}</td>
                                <td>${dc.type === 'percent' ? dc.amount + '%' : '$' + dc.amount}</td>
                                <td>${dc.current_uses || 0}${dc.max_uses ? '/' + dc.max_uses : ''}</td>
                                <td>${dc.type === 'percent' && dc.amount >= 100 ? (dc.grants_tier || 'pro') + (dc.grants_duration_days ? ' (' + dc.grants_duration_days + 'd)' : ' (perm)') : '\u2014'}</td>
                                <td>${dc.active ? 'Yes' : 'No'}</td>
                                <td>
                                    <button class="btn-ghost sm admin-dc-toggle" data-id="${dc.id}" data-active="${dc.active}">${dc.active ? 'Disable' : 'Enable'}</button>
                                    <button class="btn-ghost sm admin-dc-delete" data-id="${dc.id}">Delete</button>
                                </td>
                            </tr>`).join('')}
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="admin-section" style="margin-top:24px;">
                <h2>Utilities</h2>
                <div style="display:flex;gap:12px;align-items:center;">
                    <button class="btn btn-glass btn-sm" id="admin-re-extract-btn">Re-Extract Vault Documents</button>
                    <span id="admin-re-extract-result" style="font-size:12px;color:var(--text-muted);"></span>
                </div>
            </div>
            ${this.renderStoragePanel()}
            ${this.renderOpsJobsPanel()}
            ${this.renderDlqPanel()}
            ${this.renderIntegrationHealthPanel()}
            `}
        </div>
        ${this.renderPasswordModal()}`;
        this.bindEvents();
    }

    // R6 Stage 3 — Background jobs (Ops): last run / outcome / error per job,
    // enabled-flag state, and a "Run now" trigger for safe production testing
    // before a schedule flag is flipped.
    renderOpsJobsPanel() {
        if (!this.opsJobs) return '';
        const jobs = this.opsJobs.jobs || [];
        const fmtTime = (t) => t ? new Date(t).toLocaleString() : '—';
        const statusBadge = (j) => {
            if (j.running_now) return '<span class="role-badge" style="background:var(--primary);color:var(--on-accent);">running…</span>';
            if (j.last_status === 'success') return '<span class="role-badge" style="background:var(--success);color:var(--on-accent);">success</span>';
            if (j.last_status === 'failed') return '<span class="role-badge" style="background:var(--danger);color:var(--on-accent);">failed</span>';
            if (j.last_status === 'running') return '<span class="role-badge">running</span>';
            return '<span class="role-badge" style="color:var(--text-muted);">never run</span>';
        };
        const rows = jobs.map(j => `
            <tr>
              <td><strong>${this._esc(j.label)}</strong><div style="font-size:11px;color:var(--text-muted);max-width:320px;">${this._esc(j.description)}</div></td>
              <td>${j.enabled ? '<span class="role-badge" style="background:var(--success);color:var(--on-accent);">on</span>' : `<span class="role-badge" title="Set ${this._esc(j.flag)}=1 to enable">off</span>`}<div style="font-size:11px;color:var(--text-muted);">${this._esc(j.interval)}</div></td>
              <td>${statusBadge(j)}</td>
              <td style="font-size:11px;color:var(--text-muted);">${fmtTime(j.last_run_completed_at || j.last_run_started_at)}<div>${j.run_count ? j.run_count + ' run' + (j.run_count === 1 ? '' : 's') : ''}</div></td>
              <td style="font-size:11px;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${this._esc(j.last_error || '')}">${this._esc(j.last_error || '—')}</td>
              <td><button class="btn btn-secondary btn-sm" data-action="ops-run-job" data-job="${this._esc(j.name)}" ${j.running_now ? 'disabled' : ''}>Run now</button></td>
            </tr>`).join('');

        const platforms = (this.opsJobs.platforms || []);
        const platRows = platforms.map(p => `
            <tr>
              <td><span class="role-badge">${this._esc(p.platform)}</span></td>
              <td>${p.brands}</td>
              <td>${p.failing > 0 ? `<span style="color:var(--danger-text);font-weight:600;">${p.failing}</span>` : '0'}</td>
              <td style="font-size:11px;color:var(--text-muted);">${p.last_completed_at ? new Date(p.last_completed_at).toLocaleString() : '—'}</td>
              <td style="font-size:11px;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${this._esc(p.sample_error || '')}">${this._esc(p.sample_error || '—')}</td>
            </tr>`).join('');

        return `
            <div class="admin-section" style="margin-top:24px;">
                <div class="admin-section-header" style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
                    <h3>Background Jobs — Ops</h3>
                    <span style="color:var(--text-muted);font-size:12px;">Engagement pipeline jobs. "Run now" executes once and records the outcome; flags control the schedule.</span>
                </div>
                <table class="admin-table" style="font-size:12px;">
                    <thead><tr><th>Job</th><th>Schedule</th><th>Last outcome</th><th>Last run</th><th>Last error</th><th></th></tr></thead>
                    <tbody>${rows}</tbody>
                </table>
                <div id="ops-run-result" style="font-size:12px;color:var(--text-muted);margin-top:8px;"></div>
                ${platRows ? `
                <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);margin:14px 0 6px;">Engagement sync — per platform</div>
                <table class="admin-table" style="font-size:12px;">
                    <thead><tr><th>Platform</th><th>Brands tracked</th><th>Failing</th><th>Last synced</th><th>Sample error</th></tr></thead>
                    <tbody>${platRows}</tbody>
                </table>` : ''}
                ${this._renderOpsExtras()}
            </div>
        `;
    }

    // Session 1 / R7 — tracked errors, LLM health, migration status, test alert.
    _renderOpsExtras() {
        const errors = this.opsJobs?.errors || [];
        const llm = this.opsJobs?.llm || [];
        const mig = this.opsJobs?.migrations;

        const errRows = errors.map(e => `
            <tr>
              <td><span class="role-badge">${this._esc(e.category)}</span></td>
              <td style="font-size:11px;max-width:380px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${this._esc(e.message || '')}">${this._esc(e.message || '')}</td>
              <td>${e.count}</td>
              <td style="font-size:11px;color:var(--text-muted);">${e.last_seen_at ? new Date(e.last_seen_at).toLocaleString() : '—'}</td>
            </tr>`).join('');

        const llmLine = llm.length
            ? llm.map(r => `${this._esc(r.provider)}/${this._esc(r.outcome)}: ${r.count}`).join(' · ')
            : 'No server-side LLM calls in the last 7 days. (Counts only generations run by this server — content drafted through the MCP connector uses the AI chat’s own model and never appears here.)';

        const migLine = mig
            ? `PRAGMA user_version ${mig.user_version} · highest migration block ${mig.latest_block} (full ledger is roadmap R4)`
            : 'unavailable';

        return `
            <div style="display:flex;align-items:center;gap:12px;margin:16px 0 6px;">
              <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);">Tracked errors (deduped)</div>
              <button class="btn btn-secondary btn-sm" id="ops-test-alert">Send test alert</button>
              <span id="ops-test-alert-result" style="font-size:12px;color:var(--text-muted);"></span>
            </div>
            ${errRows ? `
            <table class="admin-table" style="font-size:12px;">
                <thead><tr><th>Category</th><th>Message</th><th>Count</th><th>Last seen</th></tr></thead>
                <tbody>${errRows}</tbody>
            </table>` : '<div style="color:var(--text-muted);font-size:12px;">No errors tracked yet — that is either great news or the pipeline is new. Use "Send test alert" to prove it works.</div>'}
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);margin:14px 0 4px;">LLM calls (7 days)</div>
            <div style="font-size:12px;">${llmLine}</div>
            <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);margin:14px 0 4px;">Migrations</div>
            <div style="font-size:12px;">${migLine}</div>
        `;
    }

    renderDlqPanel() {
        if (!this.dlq) return '';
        const t = this.dlq.totals || {};
        const byPlat = (this.dlq.byPlatform || []).map(r => `<span class="role-badge" style="margin-right:6px;">${r.platform}: ${r.cnt}</span>`).join('') || '<span style="color:var(--text-muted);">All clear</span>';
        const byErr = (this.dlq.byError || []).slice(0, 5).map(r => `<div style="display:flex;justify-content:space-between;gap:12px;font-size:12px;padding:4px 0;border-bottom:1px solid var(--border);"><code style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:480px;display:inline-block;">${this._esc(r.err)}</code><strong>${r.cnt}</strong></div>`).join('') || '<div style="color:var(--text-muted);font-size:12px;padding:6px 0;">No dead-letter posts.</div>';
        const recent = (this.dlq.recent || []).slice(0, 10).map(r => `
            <tr>
              <td><code style="font-size:11px;">${this._esc(r.id?.slice(0,8) || '')}</code></td>
              <td>${this._esc(r.brand_name || '—')}</td>
              <td><span class="role-badge">${this._esc(r.platform || '—')}</span></td>
              <td style="font-size:11px;color:var(--text-muted);">${r.scheduled_for ? new Date(r.scheduled_for).toLocaleString() : '—'}</td>
              <td>${r.retry_count ?? 0}</td>
              <td style="font-size:11px;max-width:280px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${this._esc(r.last_error || '')}">${this._esc(r.last_error || '—')}</td>
              <td><button class="btn btn-secondary btn-sm" data-action="dlq-retry" data-id="${this._esc(r.id)}">Retry</button></td>
            </tr>`).join('');
        return `
            <div class="admin-section" style="margin-top:24px;">
                <div class="admin-section-header" style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
                    <h3>Publish Queue \u2014 Dead Letter</h3>
                    <span style="color:var(--text-muted);font-size:12px;flex:1;">Posts that exhausted automatic retries. Investigate the integration before re-arming.</span>
                    ${(t.dead_letter || 0) > 0 ? `<button class="btn btn-secondary btn-sm" data-action="dlq-retry-all">Retry all (${t.dead_letter})</button>` : ''}
                </div>
                <div class="admin-stats-grid" style="margin-bottom:12px;">
                    <div class="admin-stat-card"><div class="admin-stat-num">${t.dead_letter || 0}</div><div class="admin-stat-label">Dead-letter</div></div>
                    <div class="admin-stat-card"><div class="admin-stat-num">${t.failed || 0}</div><div class="admin-stat-label">Currently failed</div></div>
                </div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:12px;">
                    <div><div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:6px;">By platform</div>${byPlat}</div>
                    <div><div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:6px;">Top errors</div>${byErr}</div>
                </div>
                ${recent ? `
                <table class="admin-table" style="margin-top:8px;font-size:12px;">
                    <thead><tr><th>ID</th><th>Brand</th><th>Platform</th><th>Scheduled</th><th>Retries</th><th>Last error</th><th></th></tr></thead>
                    <tbody>${recent}</tbody>
                </table>` : ''}
            </div>
        `;
    }

    _esc(s) {
        if (s == null) return '';
        return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }

    // Phase 35 — admin widget for integrations whose OAuth token has expired
    // and which now need a user-driven reconnect. Surfaces the platform mix and
    // the actual user emails so an operator can proactively reach out.
    renderIntegrationHealthPanel() {
        if (!this.integHealth) return '';
        const t = this.integHealth.totals || {};
        // No expired tokens? Render a tiny "all clear" line so the operator
        // doesn't wonder if the widget is broken.
        if (!t.auth_required) {
            return `
            <div class="admin-section" style="margin-top:24px;">
                <div class="admin-section-header"><h3>Integrations \u2014 Auth Health</h3></div>
                <div style="font-size:12px;color:var(--text-muted);padding:6px 0;">All connected integrations are healthy. No tokens expired.</div>
            </div>`;
        }
        const byProv = (this.integHealth.byProvider || []).map(r => `<span class="role-badge" style="margin-right:6px;">${this._esc(r.provider)}: ${r.cnt}</span>`).join('');
        const recent = (this.integHealth.recent || []).slice(0, 25).map(r => `
            <tr>
              <td><span class="role-badge">${this._esc(r.provider)}</span></td>
              <td>${this._esc(r.brand_name || '\u2014')}</td>
              <td><a href="mailto:${this._esc(r.owner_email || '')}" style="color:var(--accent);">${this._esc(r.owner_email || '\u2014')}</a></td>
              <td>${this._esc(r.account_label || '')}</td>
              <td style="text-align:center;">${r.consecutive_auth_failures}</td>
              <td style="font-size:11px;color:var(--text-muted);">${r.last_auth_failure_at ? new Date(r.last_auth_failure_at).toLocaleString() : '\u2014'}</td>
            </tr>`).join('');
        return `
            <div class="admin-section" style="margin-top:24px;">
                <div class="admin-section-header" style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
                    <h3>Integrations \u2014 Auth Health</h3>
                    <span style="color:var(--text-muted);font-size:12px;">Tokens marked auth_required after 3 consecutive 401/403s. The user must reconnect via Settings \u2192 Integrations.</span>
                </div>
                <div class="admin-stats-grid" style="margin-bottom:12px;">
                    <div class="admin-stat-card"><div class="admin-stat-num">${t.auth_required}</div><div class="admin-stat-label">Need reconnect</div></div>
                </div>
                <div style="margin-bottom:12px;">
                    <div style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:6px;">By provider</div>
                    ${byProv || '<span style="color:var(--text-muted);">\u2014</span>'}
                </div>
                ${recent ? `
                <table class="admin-table" style="margin-top:8px;font-size:12px;">
                    <thead><tr><th>Provider</th><th>Brand</th><th>Owner</th><th>Account</th><th>Failures</th><th>Last failure</th></tr></thead>
                    <tbody>${recent}</tbody>
                </table>` : ''}
            </div>
        `;
    }

    renderUserRow(u) {
        const status = u.account_status || 'active';
        const isDeleted = status === 'deleted';
        const isArchived = status === 'archived';
        const isGodAdminUser = u.role === 'god_admin';
        const isOnline = u.last_active_at && (Date.now() - new Date(u.last_active_at).getTime()) < 15 * 60 * 1000;
        const statusOptions = ['active', 'suspended', 'inactive', 'archived'];
        if (this.isGodOrAngel) statusOptions.push('deleted');
        const roleOptions = ['user', 'admin'];
        if (this.isGodAdmin) roleOptions.push('angel_admin');
        const lastLogin = u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never';
        // T-03 — signup source + email-verification state
        const sourceTip = [
            u.signup_utm_content ? `content: ${u.signup_utm_content}` : '',
            u.signup_referrer ? `ref: ${u.signup_referrer}` : '',
            u.signup_landing_url ? `landed: ${u.signup_landing_url}` : ''
        ].filter(Boolean).join('\n');
        const sourceCell = u.signup_source
            ? `<span title="${this.esc(sourceTip)}">${this.esc(u.signup_source)}</span>`
            : '<span style="opacity:0.4">—</span>';
        const verifiedCell = u.email_verified
            ? `<span title="${u.verified_at ? 'Verified ' + new Date(u.verified_at).toLocaleString() : 'Verified (pre-tracking)'}" style="color:var(--success,#22c55e)">&#10003;</span>`
            : (u.verification_sent_at
                ? `<span title="Verification sent ${new Date(u.verification_sent_at).toLocaleString()}, never verified" style="color:var(--warning,#eab308)">sent</span>`
                : '<span style="opacity:0.4" title="No verification email recorded">—</span>');
        const showGrantFree = this.grantFreeUserId === u.id;
        const isChecked = this.selectedIds.has(u.id);

        return `
        <tr data-user-id="${u.id}" class="${isDeleted ? 'admin-row-deleted' : ''} ${isChecked ? 'admin-row-selected' : ''}">
            <td style="text-align:center;">
                ${!isGodAdminUser ? `<input type="checkbox" class="admin-row-checkbox" data-user-id="${u.id}" ${isChecked ? 'checked' : ''} />` : ''}
            </td>
            <td><span class="online-dot ${isOnline ? 'online-dot--active' : 'online-dot--offline'}"></span></td>
            <td><span class="admin-email" title="${this.esc(u.email)}">${this.esc(u.email)}</span></td>
            <td><span class="admin-name" title="${this.esc(u.name)}">${this.esc(u.name)}</span></td>
            <td>
                ${(this.isGodOrAngel) && !isGodAdminUser ? `
                    <select class="admin-role-select" data-user-id="${u.id}">
                        ${roleOptions.map(r => `<option value="${r}" ${u.role === r ? 'selected' : ''}>${r}</option>`).join('')}
                    </select>
                ` : `<span class="admin-role-badge admin-role-badge--${u.role}">${u.role}</span>`}
            </td>
            <td>
                <select class="admin-tier-select" data-user-id="${u.id}" data-field="tier">
                    ${['trial','starter','pro','founder','agency'].map(t =>
                        `<option value="${t}" ${(u.tier || 'trial') === t ? 'selected' : ''}>${t}</option>`
                    ).join('')}
                </select>
            </td>
            <td>
                <select class="admin-billing-select" data-user-id="${u.id}" data-field="billing_cycle">
                    <option value="monthly" ${(u.billing_cycle || 'monthly') === 'monthly' ? 'selected' : ''}>Monthly</option>
                    <option value="annual" ${u.billing_cycle === 'annual' ? 'selected' : ''}>Annual</option>
                </select>
            </td>
            <td>
                ${!isGodAdminUser && this.currentTab === 'active' ? `
                    <select class="admin-status-select" data-user-id="${u.id}">
                        ${statusOptions.map(s => `<option value="${s}" ${status === s ? 'selected' : ''}>${s}</option>`).join('')}
                    </select>
                ` : `<span class="admin-status-badge admin-status-badge--${status}">${status}</span>`}
            </td>
            <td>${u.post_count || 0}</td>
            <td>${sourceCell}</td>
            <td>${verifiedCell}</td>
            <td>${lastLogin}</td>
            <td>${u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
            <td class="col-actions-cell">
                <button class="admin-action-btn admin-reset-pwd-btn" data-user-id="${u.id}">Reset Pwd</button>
                ${this.isGodOrAngel && !isGodAdminUser ? `<button class="admin-action-btn admin-impersonate-btn admin-action-btn--login-as" data-user-id="${u.id}">Login As</button>` : ''}
                <button class="admin-action-btn admin-grant-trial-btn" data-user-id="${u.id}">Trial</button>
                ${this.isGodOrAngel && !isGodAdminUser ? `
                    ${showGrantFree ? `
                        <input type="date" class="admin-free-input admin-grant-free-date" data-user-id="${u.id}" />
                        <button class="admin-action-btn admin-grant-free-confirm" data-user-id="${u.id}">OK</button>
                    ` : `<button class="admin-action-btn admin-grant-free-btn" data-user-id="${u.id}">Free</button>`}
                ` : ''}
                ${(isDeleted || isArchived) && this.isGodOrAngel ? `<button class="admin-action-btn admin-restore-btn" data-user-id="${u.id}">Restore</button>` : ''}
                ${isDeleted && this.isGodAdmin ? `<button class="admin-action-btn admin-action-btn--danger admin-perm-delete-btn" data-user-id="${u.id}">Perm Delete</button>` : ''}
            </td>
        </tr>`;
    }

    renderFeedbackTab() {
        const counts = this.feedbackCounts || { open: 0, in_progress: 0, resolved: 0 };
        const total = counts.open + counts.in_progress + counts.resolved;
        const CATEGORY_LABELS = { bug: "Something's broken", feature: 'Feature request', question: 'Question', other: 'Other' };
        const fmtDate = (t) => t ? new Date(t + (t.includes('Z') || t.includes('+') ? '' : 'Z')).toLocaleString() : '—';
        return `
            <div class="admin-section" data-testid="admin-feedback">
                <div class="admin-toolbar" style="margin-bottom:12px;">
                    <select id="admin-feedback-filter" class="admin-filter-select" title="Filter by status">
                        <option value="" ${this.feedbackFilter === '' ? 'selected' : ''}>All (${total})</option>
                        <option value="open" ${this.feedbackFilter === 'open' ? 'selected' : ''}>Open (${counts.open})</option>
                        <option value="in_progress" ${this.feedbackFilter === 'in_progress' ? 'selected' : ''}>In Progress (${counts.in_progress})</option>
                        <option value="resolved" ${this.feedbackFilter === 'resolved' ? 'selected' : ''}>Resolved (${counts.resolved})</option>
                    </select>
                    <span class="admin-result-count">${this.feedbackItems.length} report${this.feedbackItems.length !== 1 ? 's' : ''}</span>
                </div>
                <div class="admin-table-wrap">
                    <table class="admin-table admin-feedback-table" data-testid="admin-feedback-table">
                        <thead>
                            <tr>
                                <th style="width:130px;">Date</th>
                                <th style="width:16%;">User</th>
                                <th style="width:120px;">Type</th>
                                <th>Message</th>
                                <th style="width:90px;">Page</th>
                                <th style="width:120px;">Status</th>
                                <th style="width:16%;">Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this.feedbackItems.length === 0
                                ? `<tr><td colspan="7" style="text-align:center;color:var(--text-muted);padding:24px;">No ${this.feedbackFilter ? this.feedbackFilter.replace('_', ' ') + ' ' : ''}feedback. Quiet is good.</td></tr>`
                                : this.feedbackItems.map(f => `
                                <tr data-feedback-id="${this.esc(f.id)}">
                                    <td style="font-size:12px;color:var(--text-muted);">${fmtDate(f.created_at)}</td>
                                    <td title="${this.esc(f.user_name || '')}">${this.esc(f.user_email || f.user_id)}</td>
                                    <td><span class="admin-feedback-badge admin-feedback-badge--${this.esc(f.category)}">${this.esc(CATEGORY_LABELS[f.category] || f.category)}</span></td>
                                    <td class="admin-feedback-message">${this.esc(f.message)}</td>
                                    <td style="font-size:12px;">${f.page_context ? `<code>${this.esc(f.page_context)}</code>` : '—'}</td>
                                    <td>
                                        <select class="admin-filter-select admin-feedback-status" data-id="${this.esc(f.id)}">
                                            <option value="open" ${f.status === 'open' ? 'selected' : ''}>Open</option>
                                            <option value="in_progress" ${f.status === 'in_progress' ? 'selected' : ''}>In Progress</option>
                                            <option value="resolved" ${f.status === 'resolved' ? 'selected' : ''}>Resolved</option>
                                        </select>
                                    </td>
                                    <td><input type="text" class="input admin-feedback-note" data-id="${this.esc(f.id)}" value="${this.esc(f.admin_note || '')}" placeholder="Triage note…" /></td>
                                </tr>`).join('')}
                        </tbody>
                    </table>
                </div>
            </div>`;
    }

    renderWorkspacesTab() {
        const totalPages = Math.max(1, Math.ceil(this.totalWorkspaces / this.wsPageSize));
        return `
        <div class="admin-stats-grid" style="margin-bottom:16px;">
            <div class="admin-stat-card"><div class="admin-stat-value">${this.totalWorkspaces}</div><div class="admin-stat-label">Total Workspaces</div></div>
        </div>

        <div class="admin-toolbar">
            <div class="admin-search-wrap">
                <input type="text" id="admin-ws-search" class="admin-search-input" placeholder="Search workspaces..." value="${this.esc(this.wsSearch)}" />
            </div>
            <button class="btn-primary sm" id="admin-ws-create-btn">+ Create Workspace</button>
        </div>

        ${this.showCreateWorkspace ? `
        <div class="admin-section" style="margin-bottom:16px;">
            <div class="invite-form" style="margin-top:0;">
                <input type="text" id="admin-ws-new-email" class="input" placeholder="User email" />
                <input type="text" id="admin-ws-new-name" class="input" placeholder="Workspace name" value="Agency Workspace" />
                <button class="btn-primary sm" id="admin-ws-create-confirm">Create</button>
                <button class="admin-action-btn" id="admin-ws-create-cancel">Cancel</button>
            </div>
        </div>` : ''}

        <div class="admin-section">
            <div class="admin-table-wrap">
                <table class="admin-table">
                    <thead>
                        <tr>
                            <th style="width:25%">Name</th>
                            <th style="width:22%">Owner</th>
                            <th style="width:10%">Tier</th>
                            <th style="width:10%">Members</th>
                            <th style="width:10%">Brands</th>
                            <th style="width:13%">Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this.workspaces.map(ws => `
                            <tr data-ws-id="${ws.id}" class="${this.expandedWorkspaceId === ws.id ? 'admin-row-selected' : ''}">
                                <td><strong>${this.esc(ws.name)}</strong></td>
                                <td><span title="${this.esc(ws.owner_email)}">${this.esc(ws.owner_email)}</span></td>
                                <td><span class="admin-role-badge">${ws.tier || 'agency'}</span></td>
                                <td>${ws.member_count || 0} / ${ws.seat_limit || 10}</td>
                                <td>${ws.brand_count || 0}</td>
                                <td>${ws.created_at ? new Date(ws.created_at).toLocaleDateString() : '-'}</td>
                                <td>
                                    <button class="admin-action-btn admin-ws-expand-btn" data-ws-id="${ws.id}">${this.expandedWorkspaceId === ws.id ? 'Collapse' : 'Manage'}</button>
                                </td>
                            </tr>
                            ${this.expandedWorkspaceId === ws.id && this.expandedWorkspaceData ? `
                            <tr><td colspan="7" style="padding:0;border:none;">
                                ${this.renderWorkspaceDetail()}
                            </td></tr>` : ''}
                        `).join('')}
                        ${this.workspaces.length === 0 ? '<tr><td colspan="7" style="text-align:center;padding:32px;color:var(--text-muted);">No workspaces found</td></tr>' : ''}
                    </tbody>
                </table>
            </div>
            ${totalPages > 1 ? `
            <div class="admin-pagination">
                <button class="admin-page-btn admin-ws-page-btn" data-page="${this.wsCurrentPage - 1}" ${this.wsCurrentPage <= 1 ? 'disabled' : ''}>← Prev</button>
                <span class="admin-page-info">Page ${this.wsCurrentPage} of ${totalPages}</span>
                <button class="admin-page-btn admin-ws-page-btn" data-page="${this.wsCurrentPage + 1}" ${this.wsCurrentPage >= totalPages ? 'disabled' : ''}>Next →</button>
            </div>` : ''}
        </div>`;
    }

    renderWorkspaceDetail() {
        const d = this.expandedWorkspaceData;
        if (!d) return '';
        const ws = d.workspace;
        const brain = d.agencyBrain || {};
        const members = d.members || [];
        const brands = d.brands || [];
        const seatPct = ws.seatLimit ? Math.round((ws.seatsUsed / ws.seatLimit) * 100) : 0;
        const seatClass = seatPct >= 100 ? 'seat-bar__fill--full' : seatPct >= 80 ? 'seat-bar__fill--warn' : '';

        return `
        <div class="admin-ws-detail">
            <!-- Settings -->
            <div class="admin-ws-detail__section">
                <h3>Workspace Settings</h3>
                <div class="invite-form" style="margin-top:8px;">
                    <input type="text" id="admin-ws-edit-name" class="input" value="${this.esc(ws.name)}" placeholder="Workspace name" />
                    <select id="admin-ws-edit-tier" class="input">
                        ${['trial','starter','pro','founder','agency','admin'].map(t =>
                            `<option value="${t}" ${ws.tier === t ? 'selected' : ''}>${t}</option>`
                        ).join('')}
                    </select>
                    <input type="number" id="admin-ws-edit-seats" class="input" value="${ws.seatLimit}" min="1" max="999" style="width:80px;" placeholder="Seats" />
                    <button class="btn-primary sm" id="admin-ws-save-settings" data-ws-id="${ws.id}">Save</button>
                </div>
                <div style="margin-top:8px;">
                    <div style="font-size:12px;color:var(--text-muted);">${ws.seatsUsed} / ${ws.seatLimit} seats used</div>
                    <div class="seat-bar"><div class="seat-bar__fill ${seatClass}" style="width:${Math.min(100, seatPct)}%"></div></div>
                </div>
            </div>

            <!-- Members -->
            <div class="admin-ws-detail__section">
                <h3>Members (${members.length})</h3>
                <div class="team-member-list">
                    ${members.map(m => {
                        const initials = (m.name || m.email || '?').split(/\s/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
                        const isOwner = m.role === 'owner';
                        return `
                        <div class="team-member-card">
                            <div class="team-member-card__avatar">${initials}</div>
                            <div class="team-member-card__info">
                                <div class="team-member-card__name">${this.esc(m.name || 'Unknown')}</div>
                                <div class="team-member-card__email">${this.esc(m.email)}</div>
                            </div>
                            <span class="role-badge role-badge--${m.role}">${m.role}</span>
                            <div class="team-member-card__actions">
                                ${!isOwner ? `
                                    <select class="admin-ws-member-role" data-ws-id="${ws.id}" data-user-id="${m.user_id}" style="font-size:12px;padding:4px;">
                                        ${['admin','editor','viewer'].map(r => `<option value="${r}" ${m.role === r ? 'selected' : ''}>${r}</option>`).join('')}
                                    </select>
                                    <button class="admin-action-btn admin-ws-remove-member" data-ws-id="${ws.id}" data-user-id="${m.user_id}">Remove</button>
                                ` : ''}
                                <button class="admin-action-btn admin-action-btn--login-as admin-ws-login-as" data-user-id="${m.user_id}">Login As</button>
                            </div>
                        </div>`;
                    }).join('')}
                </div>
                <div class="invite-form">
                    <input type="text" id="admin-ws-add-email" class="input" placeholder="User email to add" />
                    <select id="admin-ws-add-role" class="input" style="min-width:100px;">
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                        <option value="viewer">Viewer</option>
                    </select>
                    <button class="btn-primary sm" id="admin-ws-add-member-btn" data-ws-id="${ws.id}">Add Member</button>
                </div>
            </div>

            <!-- Brands -->
            <div class="admin-ws-detail__section">
                <h3>Brands (${brands.length})</h3>
                <div class="brands-list">
                    ${brands.map(b => `
                    <div class="brand-card">
                        <div class="brand-card__icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg></div>
                        <div class="brand-card__info">
                            <div class="brand-card__name">${this.esc(b.name)}</div>
                            <div class="brand-card__meta">Created ${b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}</div>
                        </div>
                    </div>`).join('')}
                    ${brands.length === 0 ? '<div style="color:var(--text-muted);font-size:13px;padding:12px 0;">No brands yet</div>' : ''}
                </div>
            </div>

            <!-- Agency Brain -->
            <div class="admin-ws-detail__section">
                <div class="agency-brain-card">
                    <div class="agency-brain-card__header">
                        <div class="agency-brain-card__icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></div>
                        <div>
                            <div class="agency-brain-card__title">Agency Brain</div>
                            <div class="agency-brain-card__desc">Shared brand guidelines inherited by all brands in this workspace</div>
                        </div>
                    </div>
                    ${['mission','audience','voice','offers_summary','cta','keywords','avoid_words'].map(f => `
                    <div style="margin-bottom:12px;">
                        <label style="display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);margin-bottom:4px;">${f.replace(/_/g, ' ')}</label>
                        <textarea class="input admin-ws-brain-field" data-field="${f}" rows="2" style="width:100%;resize:vertical;">${this.esc(brain[f] || '')}</textarea>
                    </div>`).join('')}
                    <button class="btn-primary sm" id="admin-ws-save-brain" data-ws-id="${ws.id}">Save Agency Brain</button>
                </div>
            </div>
        </div>`;
    }

    renderDashboardTab() {
        const d = this.dashboardSummary;
        if (!d) {
            return '<div class="admin-section"><div class="admin-loading">Loading dashboard…</div></div>';
        }

        const mrr = Number(d.mrr || 0);
        const paidByTier = d.paidByTier || {};
        const tierEntries = Object.entries(paidByTier);
        const paidBreakdown = tierEntries.length
            ? tierEntries.map(([tier, n]) => `<span class="admin-dash-chip">${this.esc(tier)}: ${n}</span>`).join('')
            : '<span class="admin-dash-chip admin-dash-chip--muted">no paid subscribers yet</span>';

        const events = this.dashboardEvents || [];
        const eventRows = events.length ? events.map(ev => {
            const when = ev.at ? new Date(ev.at).toLocaleString() : '—';
            const label = ev.type === 'converted' ? 'Converted' : 'Signup';
            const badgeClass = ev.type === 'converted' ? 'admin-badge admin-badge--paid' : (ev.has_stripe ? 'admin-badge admin-badge--paid' : 'admin-badge admin-badge--trial');
            return `
            <div class="admin-dash-event">
                <div class="admin-dash-event__left">
                    <span class="${badgeClass}">${label}</span>
                    <span class="admin-dash-event__email">${this.esc(ev.email || '')}</span>
                </div>
                <div class="admin-dash-event__right">
                    <span class="admin-dash-event__tier">${this.esc(ev.tier || '')}</span>
                    <span class="admin-dash-event__at">${when}</span>
                </div>
            </div>`;
        }).join('') : '<div class="admin-dash-event admin-dash-event--empty">No events in the last 30 days.</div>';

        return `
        <div class="admin-section admin-dashboard">
            <div class="admin-dashboard__cards">
                <div class="admin-stat-card admin-dash-card">
                    <div class="admin-stat-label">MRR</div>
                    <div class="admin-stat-value">$${mrr.toLocaleString()}</div>
                    <div class="admin-dash-hint">${d.paidTotal || 0} paid subscriber${d.paidTotal === 1 ? '' : 's'}</div>
                </div>
                <div class="admin-stat-card admin-dash-card">
                    <div class="admin-stat-label">Total users</div>
                    <div class="admin-stat-value">${d.totalUsers || 0}</div>
                    <div class="admin-dash-hint">${d.signupsLast7Days || 0} in last 7 days</div>
                </div>
                <div class="admin-stat-card admin-dash-card">
                    <div class="admin-stat-label">Active trials</div>
                    <div class="admin-stat-value">${d.activeTrials || 0}</div>
                    <div class="admin-dash-hint">${d.expiringWithin24h || 0} expiring in 24h</div>
                </div>
                <div class="admin-stat-card admin-dash-card">
                    <div class="admin-stat-label">Expired (unpaid)</div>
                    <div class="admin-stat-value">${d.expiredNonPaying || 0}</div>
                    <div class="admin-dash-hint">${tierEntries.length ? 'upgrade prompts in flight' : '&nbsp;'}</div>
                </div>
            </div>

            <div class="admin-dashboard__breakdown">
                <div class="admin-dashboard__section-head">
                    <h3>Paid breakdown</h3>
                    <button class="admin-btn-ghost" id="admin-dash-refresh">Refresh</button>
                </div>
                <div class="admin-dash-chips">${paidBreakdown}</div>
            </div>

            ${this.renderFunnelTable()}

            <div class="admin-dashboard__events">
                <div class="admin-dashboard__section-head">
                    <h3>Recent events <span class="admin-dash-count">${events.length}</span></h3>
                </div>
                <div class="admin-dash-events-list">${eventRows}</div>
            </div>
        </div>
        <style>
          .admin-dashboard__funnel { margin-top:20px; padding:16px; background:var(--bg-soft, rgba(255,255,255,0.02)); border:1px solid var(--border); border-radius:10px; }
          .admin-funnel-table { width:100%; border-collapse:collapse; font-size:12px; }
          .admin-funnel-table th, .admin-funnel-table td { padding:6px 8px; text-align:left; border-bottom:1px solid var(--border); white-space:nowrap; }
          .admin-funnel-table th { font-size:10px; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
          .admin-funnel-pct { font-size:10px; color:var(--text-muted); }
        </style>
        <style>
          .admin-dashboard__cards { display:grid; grid-template-columns:repeat(auto-fit, minmax(200px, 1fr)); gap:12px; margin-bottom:24px; }
          .admin-dash-card .admin-stat-label { font-size:11px; text-transform:uppercase; letter-spacing:0.08em; color:var(--text-muted); margin-bottom:6px; }
          .admin-dash-card .admin-stat-value { font-size:28px; font-weight:700; color:var(--text); margin-bottom:4px; }
          .admin-dash-hint { font-size:12px; color:var(--text-muted); }
          .admin-dashboard__breakdown, .admin-dashboard__events { margin-top:20px; padding:16px; background:var(--bg-soft, rgba(255,255,255,0.02)); border:1px solid var(--border); border-radius:10px; }
          .admin-dashboard__section-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
          .admin-dashboard__section-head h3 { margin:0; font-size:14px; font-weight:600; color:var(--text); }
          .admin-dash-count { font-size:11px; color:var(--text-muted); font-weight:400; margin-left:6px; }
          .admin-dash-chips { display:flex; flex-wrap:wrap; gap:6px; }
          .admin-dash-chip { display:inline-flex; padding:4px 10px; border-radius:999px; background:rgba(139,92,246,0.12); color:#A78BFA; font-size:12px; font-weight:500; }
          .admin-dash-chip--muted { background:rgba(255,255,255,0.04); color:var(--text-muted); }
          .admin-dash-events-list { display:flex; flex-direction:column; gap:4px; max-height:480px; overflow-y:auto; }
          .admin-dash-event { display:flex; justify-content:space-between; align-items:center; padding:8px 10px; border-radius:6px; background:rgba(255,255,255,0.02); font-size:13px; }
          .admin-dash-event:hover { background:rgba(139,92,246,0.06); }
          .admin-dash-event--empty { justify-content:center; color:var(--text-muted); padding:20px; }
          .admin-dash-event__left, .admin-dash-event__right { display:flex; align-items:center; gap:10px; }
          .admin-dash-event__email { color:var(--text); }
          .admin-dash-event__tier, .admin-dash-event__at { font-size:12px; color:var(--text-muted); }
          .admin-badge { display:inline-flex; padding:2px 8px; border-radius:4px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; }
          .admin-badge--trial { background:rgba(234,179,8,0.12); color:#EAB308; }
          .admin-badge--paid { background:rgba(34,197,94,0.12); color:#22C55E; }
          .admin-btn-ghost { padding:4px 10px; border-radius:6px; background:transparent; border:1px solid var(--border); color:var(--text-muted); font-size:12px; cursor:pointer; }
          .admin-btn-ghost:hover { color:var(--text); border-color:var(--text-muted); }
        </style>`;
    }

    bindEvents() {
        // Dead-letter queue retry buttons (BE-10)
        this.el.querySelectorAll('button[data-action="dlq-retry"]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = e.currentTarget.dataset.id;
                if (!id) return;
                e.currentTarget.disabled = true;
                e.currentTarget.textContent = 'Retrying...';
                try {
                    const res = await client.adminRetryDeadLetter(id);
                    if (res?.ok) {
                        toast('success', 'Post re-armed for retry. The job runner will pick it up shortly.');
                        // Refresh DLQ to drop the row from the table.
                        const fresh = await client.adminGetDeadLetter();
                        if (fresh?.ok) { this.dlq = fresh; this.render(); }
                    } else {
                        toast('error', res?.error || 'Retry failed');
                        e.currentTarget.disabled = false;
                        e.currentTarget.textContent = 'Retry';
                    }
                } catch (err) {
                    toast('error', err.message || 'Retry failed');
                    e.currentTarget.disabled = false;
                    e.currentTarget.textContent = 'Retry';
                }
            });
        });

        // T-08 — re-arm the whole dead-letter queue in one click.
        this.el.querySelectorAll('button[data-action="dlq-retry-all"]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                if (!window.confirm('Re-arm ALL dead-letter posts for retry? Fix the failing integration first — otherwise they will cycle straight back here.')) return;
                e.currentTarget.disabled = true;
                e.currentTarget.textContent = 'Retrying all…';
                try {
                    const res = await client.adminRetryAllDeadLetter();
                    if (res?.ok) {
                        toast('success', `${res.retried} post${res.retried === 1 ? '' : 's'} re-armed. The job runner will pick them up shortly.`);
                        const fresh = await client.adminGetDeadLetter();
                        if (fresh?.ok) { this.dlq = fresh; this.render(); }
                    } else {
                        toast('error', res?.error || 'Retry-all failed');
                        e.currentTarget.disabled = false;
                        e.currentTarget.textContent = 'Retry all';
                    }
                } catch (err) {
                    toast('error', err.message || 'Retry-all failed');
                    e.currentTarget.disabled = false;
                    e.currentTarget.textContent = 'Retry all';
                }
            });
        });

        // R6 Stage 3 — Ops: run a background job once, now.
        this.el.querySelectorAll('button[data-action="ops-run-job"]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const name = e.currentTarget.dataset.job;
                e.currentTarget.disabled = true;
                e.currentTarget.textContent = 'Running…';
                const resultEl = this.el.querySelector('#ops-run-result');
                try {
                    const res = await client.adminRunOpsJob(name);
                    if (res?.ok && res.status === 'success') {
                        toast('success', `${name} completed.`);
                        if (resultEl) resultEl.textContent = `${name}: ${JSON.stringify(res.summary || {}).slice(0, 300)}`;
                    } else {
                        toast('error', `${name} failed: ${res?.error || 'see Ops panel'}`);
                        if (resultEl) resultEl.textContent = `${name} error: ${res?.error || 'unknown'}`;
                    }
                } catch (err) {
                    toast('error', err.message || 'Job run failed');
                } finally {
                    const fresh = await client.adminGetOpsJobs().catch(() => null);
                    if (fresh?.ok) { this.opsJobs = fresh; this.render(); }
                }
            });
        });

        // Session 1 / R7 — test alert button.
        const testAlertBtn = this.el.querySelector('#ops-test-alert');
        if (testAlertBtn) {
            testAlertBtn.addEventListener('click', async () => {
                testAlertBtn.disabled = true;
                const out = this.el.querySelector('#ops-test-alert-result');
                try {
                    const res = await client.adminOpsTestAlert();
                    if (out) out.textContent = res?.ok ? 'Alert fired — check email + the errors table after refresh.' : (res?.error || 'failed');
                } catch (err) {
                    if (out) out.textContent = err.message || 'failed';
                } finally {
                    testAlertBtn.disabled = false;
                }
            });
        }

        // Tab switching
        this.el.querySelectorAll('.admin-tab').forEach(tab => {
            tab.addEventListener('click', async (e) => {
                this.currentTab = e.target.dataset.tab;
                this.currentPage = 1;
                this.selectedIds.clear();
                if (this.currentTab === 'workspaces') {
                    await this.loadWorkspaces();
                } else if (this.currentTab === 'feedback') {
                    await this.loadFeedback();
                } else {
                    this.expandedWorkspaceId = null;
                    this.expandedWorkspaceData = null;
                    await this.loadData();
                }
                this.render();
            });
        });

        // Feedback tab: status filter
        const fbFilter = this.el.querySelector('#admin-feedback-filter');
        if (fbFilter) {
            fbFilter.addEventListener('change', async (e) => {
                this.feedbackFilter = e.target.value;
                await this.loadFeedback();
                this.render();
            });
        }

        // Feedback tab: status change per report
        this.el.querySelectorAll('.admin-feedback-status').forEach(sel => {
            sel.addEventListener('change', async (e) => {
                const id = e.target.dataset.id;
                try {
                    await client.adminUpdateFeedback(id, { status: e.target.value });
                    toast('success', 'Status updated');
                    await this.loadFeedback();
                    this.render();
                } catch (err) {
                    toast('error', err.message || 'Failed to update status');
                }
            });
        });

        // Feedback tab: triage note (saved on blur/change)
        this.el.querySelectorAll('.admin-feedback-note').forEach(inp => {
            inp.addEventListener('change', async (e) => {
                const id = e.target.dataset.id;
                try {
                    await client.adminUpdateFeedback(id, { admin_note: e.target.value.trim() });
                    toast('success', 'Note saved');
                } catch (err) {
                    toast('error', err.message || 'Failed to save note');
                }
            });
        });

        // Dashboard: manual refresh button
        const dashRefresh = this.el.querySelector('#admin-dash-refresh');
        if (dashRefresh) {
            dashRefresh.addEventListener('click', async () => {
                dashRefresh.textContent = 'Refreshing…';
                dashRefresh.disabled = true;
                try { await this.loadDashboard(); this.render(); }
                finally { /* re-render replaces the button */ }
            });
        }

        // Search
        const searchInput = this.el.querySelector('#admin-search');
        if (searchInput) {
            searchInput.addEventListener('input', (e) => {
                this.searchQuery = e.target.value;
                this.currentPage = 1;
                clearTimeout(this._searchDebounce);
                this._searchDebounce = setTimeout(async () => {
                    await this.loadData();
                    this.render();
                    const newInput = this.el.querySelector('#admin-search');
                    if (newInput) { newInput.focus(); newInput.selectionStart = newInput.selectionEnd = newInput.value.length; }
                }, 300);
            });
        }

        // Tier filter
        const tierFilter = this.el.querySelector('#admin-filter-tier');
        if (tierFilter) {
            tierFilter.addEventListener('change', async (e) => {
                this.filterTier = e.target.value;
                this.currentPage = 1;
                await this.loadData();
                this.render();
            });
        }

        // Role filter
        const roleFilter = this.el.querySelector('#admin-filter-role');
        if (roleFilter) {
            roleFilter.addEventListener('change', async (e) => {
                this.filterRole = e.target.value;
                this.currentPage = 1;
                await this.loadData();
                this.render();
            });
        }

        // Billing filter
        const billingFilter = this.el.querySelector('#admin-filter-billing');
        if (billingFilter) {
            billingFilter.addEventListener('change', async (e) => {
                this.filterBilling = e.target.value;
                this.currentPage = 1;
                await this.loadData();
                this.render();
            });
        }

        // Status filter
        const statusFilter = this.el.querySelector('#admin-filter-status');
        if (statusFilter) {
            statusFilter.addEventListener('change', async (e) => {
                this.filterStatus = e.target.value;
                this.currentPage = 1;
                await this.loadData();
                this.render();
            });
        }

        // Column sorting
        this.el.querySelectorAll('th[data-sort]').forEach(th => {
            th.addEventListener('click', async () => {
                const col = th.dataset.sort;
                if (this.sortCol === col) {
                    this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortCol = col;
                    this.sortDir = 'asc';
                }
                this.currentPage = 1;
                await this.loadData();
                this.render();
            });
        });

        // Page size
        const pageSizeSelect = this.el.querySelector('#admin-page-size');
        if (pageSizeSelect) {
            pageSizeSelect.addEventListener('change', async (e) => {
                this.pageSize = parseInt(e.target.value);
                this.currentPage = 1;
                await this.loadData();
                this.render();
            });
        }

        // Pagination
        this.el.querySelectorAll('.admin-page-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const page = parseInt(e.target.dataset.page);
                if (page >= 1) { this.currentPage = page; this.loadData().then(() => this.render()); }
            });
        });

        // Select all checkbox
        const selectAll = this.el.querySelector('.admin-select-all');
        if (selectAll) {
            selectAll.addEventListener('change', (e) => {
                const sorted = this.getSortedFilteredUsers();
                const paged = this.getPagedUsers(sorted);
                const selectableIds = paged.filter(u => u.role !== 'god_admin').map(u => u.id);
                if (e.target.checked) {
                    selectableIds.forEach(id => this.selectedIds.add(id));
                } else {
                    selectableIds.forEach(id => this.selectedIds.delete(id));
                }
                this.render();
            });
        }

        // Row checkboxes
        this.el.querySelectorAll('.admin-row-checkbox').forEach(cb => {
            cb.addEventListener('change', (e) => {
                const userId = e.target.dataset.userId;
                if (e.target.checked) {
                    this.selectedIds.add(userId);
                } else {
                    this.selectedIds.delete(userId);
                }
                this.render();
            });
        });

        // Bulk action buttons
        this.el.querySelectorAll('.admin-bulk-btn[data-bulk-action]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const action = e.target.dataset.bulkAction;
                const ids = [...this.selectedIds];
                if (ids.length === 0) return;
                if (action === 'deleted' && !confirm(`Soft-delete ${ids.length} user(s)?`)) return;
                if (action === 'archived' && !confirm(`Archive ${ids.length} user(s)?`)) return;
                if (action === 'suspended') {
                    const reason = prompt('Reason for suspension (optional):');
                    try {
                        await client.adminBulkStatus({ user_ids: ids, status: action, reason });
                        this.selectedIds.clear();
                        await this.loadData();
                        this.render();
                    } catch (err) { toast('error', err.message); }
                    return;
                }
                try {
                    await client.adminBulkStatus({ user_ids: ids, status: action });
                    this.selectedIds.clear();
                    await this.loadData();
                    this.render();
                } catch (err) { toast('error', err.message); }
            });
        });

        // Bulk permanent delete — opens password modal
        const bulkPermBtn = this.el.querySelector('#admin-bulk-perm-delete');
        if (bulkPermBtn) {
            bulkPermBtn.addEventListener('click', () => {
                this.pendingPermDeleteIds = [...this.selectedIds];
                this.showPasswordModal = true;
                this.render();
                const pwInput = this.el.querySelector('#admin-perm-delete-password');
                if (pwInput) pwInput.focus();
            });
        }

        // Clear selection
        const clearBtn = this.el.querySelector('#admin-bulk-clear');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                this.selectedIds.clear();
                this.render();
            });
        }

        // Single permanent delete button — opens password modal
        this.el.querySelectorAll('.admin-perm-delete-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                this.pendingPermDeleteIds = [e.target.dataset.userId];
                this.showPasswordModal = true;
                this.render();
                const pwInput = this.el.querySelector('#admin-perm-delete-password');
                if (pwInput) pwInput.focus();
            });
        });

        // Password modal events
        const modalOverlay = this.el.querySelector('#admin-modal-overlay');
        if (modalOverlay) {
            // Cancel
            const cancelBtn = this.el.querySelector('#admin-modal-cancel');
            if (cancelBtn) {
                cancelBtn.addEventListener('click', () => {
                    this.showPasswordModal = false;
                    this.pendingPermDeleteIds = [];
                    this.render();
                });
            }
            // Click overlay to close
            modalOverlay.addEventListener('click', (e) => {
                if (e.target === modalOverlay) {
                    this.showPasswordModal = false;
                    this.pendingPermDeleteIds = [];
                    this.render();
                }
            });
            // Confirm permanent delete
            const confirmBtn = this.el.querySelector('#admin-modal-confirm');
            if (confirmBtn) {
                confirmBtn.addEventListener('click', async () => {
                    const pwInput = this.el.querySelector('#admin-perm-delete-password');
                    const errorDiv = this.el.querySelector('#admin-modal-error');
                    const password = pwInput?.value;
                    if (!password) {
                        if (errorDiv) { errorDiv.textContent = 'Password is required'; errorDiv.style.display = 'block'; }
                        return;
                    }
                    confirmBtn.disabled = true;
                    confirmBtn.textContent = 'Deleting...';
                    try {
                        if (this.pendingPermDeleteIds.length === 1) {
                            await client.adminPermanentDelete(this.pendingPermDeleteIds[0], { password });
                        } else {
                            await client.adminBulkPermanentDelete({ user_ids: this.pendingPermDeleteIds, password });
                        }
                        this.showPasswordModal = false;
                        this.pendingPermDeleteIds = [];
                        this.selectedIds.clear();
                        await this.loadData();
                        this.render();
                    } catch (err) {
                        if (errorDiv) { errorDiv.textContent = err.message || 'Failed to delete'; errorDiv.style.display = 'block'; }
                        confirmBtn.disabled = false;
                        confirmBtn.textContent = 'Permanently Delete';
                    }
                });
            }
            // Enter key in password field
            const pwInput = this.el.querySelector('#admin-perm-delete-password');
            if (pwInput) {
                pwInput.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') {
                        const confirmBtn = this.el.querySelector('#admin-modal-confirm');
                        if (confirmBtn) confirmBtn.click();
                    }
                });
            }
        }

        // Tier dropdowns
        this.el.querySelectorAll('.admin-tier-select').forEach(sel => {
            sel.addEventListener('change', async (e) => {
                try { await client.adminUpdateTier(e.target.dataset.userId, { tier: e.target.value }); }
                catch (err) { toast('error', 'Failed to update tier: ' + err.message); }
            });
        });

        // Billing dropdowns
        this.el.querySelectorAll('.admin-billing-select').forEach(sel => {
            sel.addEventListener('change', async (e) => {
                try { await client.adminUpdateTier(e.target.dataset.userId, { billing_cycle: e.target.value }); }
                catch (err) { toast('error', 'Failed to update billing: ' + err.message); }
            });
        });

        // Role dropdowns
        this.el.querySelectorAll('.admin-role-select').forEach(sel => {
            sel.addEventListener('change', async (e) => {
                try { await client.adminUpdateTier(e.target.dataset.userId, { role: e.target.value }); }
                catch (err) { toast('error', 'Failed to update role: ' + err.message); }
            });
        });

        // Status dropdowns
        this.el.querySelectorAll('.admin-status-select').forEach(sel => {
            sel.addEventListener('change', async (e) => {
                const userId = e.target.dataset.userId;
                const status = e.target.value;
                let reason = null;
                if (status === 'suspended') reason = prompt('Reason for suspension (optional):');
                if (status === 'deleted' && !confirm('Soft-delete this account?')) { await this.loadData(); this.render(); return; }
                try { await client.adminSetStatus(userId, { status, reason }); await this.loadData(); this.render(); }
                catch (err) { toast('error', err.message); await this.loadData(); this.render(); }
            });
        });

        // Reset password
        this.el.querySelectorAll('.admin-reset-pwd-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const newPwd = prompt('Enter new password (min 8 chars):');
                if (!newPwd || newPwd.length < 8) { if (newPwd !== null) toast('error', 'Password must be at least 8 characters'); return; }
                try { await client.adminResetPassword(e.target.dataset.userId, { password: newPwd }); toast('success', 'Password reset successfully'); }
                catch (err) { toast('error', err.message); }
            });
        });

        // Impersonate
        this.el.querySelectorAll('.admin-impersonate-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const user = this.users.find(u => u.id === e.target.dataset.userId);
                if (!confirm(`Login as ${user?.email}? You can return to your account at any time using the banner.`)) return;
                try { await client.adminImpersonate(e.target.dataset.userId); window.location.reload(); }
                catch (err) { toast('error', err.message); }
            });
        });

        // Restore
        this.el.querySelectorAll('.admin-restore-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                try { await client.adminRestoreUser(e.target.dataset.userId); await this.loadData(); this.render(); }
                catch (err) { toast('error', err.message); }
            });
        });

        // Grant Trial
        this.el.querySelectorAll('.admin-grant-trial-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const user = this.users.find(u => u.id === e.target.dataset.userId);
                if (!confirm(`Grant 7-day trial to ${user?.email}?`)) return;
                try { await client.adminGrantTrial(e.target.dataset.userId); await this.loadData(); this.render(); }
                catch (err) { toast('error', err.message); }
            });
        });

        // Grant Free — toggle date picker
        this.el.querySelectorAll('.admin-grant-free-btn').forEach(btn => {
            btn.addEventListener('click', (e) => { this.grantFreeUserId = e.target.dataset.userId; this.render(); });
        });

        // Grant Free confirm
        this.el.querySelectorAll('.admin-grant-free-confirm').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const dateInput = this.el.querySelector(`.admin-grant-free-date[data-user-id="${e.target.dataset.userId}"]`);
                if (!dateInput?.value) { toast('error', 'Please select an expiry date'); return; }
                try {
                    await client.adminGrantFree(e.target.dataset.userId, { expires_at: new Date(dateInput.value).toISOString() });
                    this.grantFreeUserId = null; await this.loadData(); this.render();
                } catch (err) { toast('error', err.message); }
            });
        });

        // Discount code CRUD
        const createBtn = this.el.querySelector('#admin-dc-create');
        if (createBtn) {
            createBtn.addEventListener('click', async () => {
                const code = this.el.querySelector('#admin-dc-code').value.trim();
                const type = this.el.querySelector('#admin-dc-type').value;
                const amount = parseFloat(this.el.querySelector('#admin-dc-amount').value);
                const max_uses = parseInt(this.el.querySelector('#admin-dc-max').value) || null;
                const grants_tier = this.el.querySelector('#admin-dc-tier')?.value || 'pro';
                const grants_duration_days = parseInt(this.el.querySelector('#admin-dc-days')?.value) || null;
                if (!code || !amount) { toast('error', 'Code and amount are required'); return; }
                try { await client.discountCreate({ code, type, amount, max_uses, grants_tier, grants_duration_days }); await this.loadData(); this.render(); }
                catch (err) { toast('error', err.message); }
            });
        }

        this.el.querySelectorAll('.admin-dc-toggle').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                try { await client.discountUpdate(e.target.dataset.id, { active: e.target.dataset.active !== '1' }); await this.loadData(); this.render(); }
                catch (err) { toast('error', err.message); }
            });
        });

        // Re-extract vault
        const reExtractBtn = this.el.querySelector('#admin-re-extract-btn');
        if (reExtractBtn) {
            reExtractBtn.addEventListener('click', async () => {
                const resultEl = this.el.querySelector('#admin-re-extract-result');
                reExtractBtn.disabled = true;
                reExtractBtn.textContent = 'Extracting...';
                try {
                    const res = await client.adminReExtractVault();
                    if (resultEl) resultEl.textContent = `Done: ${res.success} extracted, ${res.failed} failed, ${res.skipped} skipped`;
                    reExtractBtn.textContent = 'Re-Extract Vault Documents';
                    reExtractBtn.disabled = false;
                } catch (err) {
                    if (resultEl) resultEl.textContent = 'Error: ' + err.message;
                    reExtractBtn.textContent = 'Re-Extract Vault Documents';
                    reExtractBtn.disabled = false;
                }
            });
        }

        this.el.querySelectorAll('.admin-dc-delete').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                if (!confirm('Delete this discount code?')) return;
                try { await client.discountDelete(e.target.dataset.id); await this.loadData(); this.render(); }
                catch (err) { toast('error', err.message); }
            });
        });

        // === WORKSPACE TAB EVENTS ===

        // Workspace search
        const wsSearch = this.el.querySelector('#admin-ws-search');
        if (wsSearch) {
            wsSearch.addEventListener('input', (e) => {
                this.wsSearch = e.target.value;
                this.wsCurrentPage = 1;
                clearTimeout(this._wsSearchDebounce);
                this._wsSearchDebounce = setTimeout(async () => {
                    await this.loadWorkspaces();
                    this.render();
                    const newInput = this.el.querySelector('#admin-ws-search');
                    if (newInput) { newInput.focus(); newInput.selectionStart = newInput.selectionEnd = newInput.value.length; }
                }, 300);
            });
        }

        // Create workspace toggle
        const wsCreateBtn = this.el.querySelector('#admin-ws-create-btn');
        if (wsCreateBtn) {
            wsCreateBtn.addEventListener('click', () => { this.showCreateWorkspace = !this.showCreateWorkspace; this.render(); });
        }
        const wsCreateCancel = this.el.querySelector('#admin-ws-create-cancel');
        if (wsCreateCancel) {
            wsCreateCancel.addEventListener('click', () => { this.showCreateWorkspace = false; this.render(); });
        }

        // Create workspace confirm
        const wsCreateConfirm = this.el.querySelector('#admin-ws-create-confirm');
        if (wsCreateConfirm) {
            wsCreateConfirm.addEventListener('click', async () => {
                const emailInput = this.el.querySelector('#admin-ws-new-email');
                const nameInput = this.el.querySelector('#admin-ws-new-name');
                const email = emailInput?.value?.trim();
                const name = nameInput?.value?.trim() || 'Agency Workspace';
                if (!email) { toast('error', 'User email is required'); return; }
                try {
                    await client.adminCreateWorkspace({ email, name });
                    this.showCreateWorkspace = false;
                    await this.loadWorkspaces();
                    this.render();
                    toast('success', 'Workspace created');
                } catch (err) { toast('error', err.message); }
            });
        }

        // Expand/collapse workspace
        this.el.querySelectorAll('.admin-ws-expand-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const wsId = e.target.dataset.wsId;
                if (this.expandedWorkspaceId === wsId) {
                    this.expandedWorkspaceId = null;
                    this.expandedWorkspaceData = null;
                    this.render();
                } else {
                    await this.loadWorkspaceDetail(wsId);
                    this.render();
                }
            });
        });

        // Workspace pagination
        this.el.querySelectorAll('.admin-ws-page-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const page = parseInt(e.target.dataset.page);
                if (page >= 1) { this.wsCurrentPage = page; await this.loadWorkspaces(); this.render(); }
            });
        });

        // Save workspace settings
        const wsSaveSettings = this.el.querySelector('#admin-ws-save-settings');
        if (wsSaveSettings) {
            wsSaveSettings.addEventListener('click', async (e) => {
                const wsId = e.target.dataset.wsId;
                const name = this.el.querySelector('#admin-ws-edit-name')?.value?.trim();
                const tier = this.el.querySelector('#admin-ws-edit-tier')?.value;
                const seat_limit = parseInt(this.el.querySelector('#admin-ws-edit-seats')?.value) || 10;
                try {
                    await client.adminUpdateWorkspace(wsId, { name, tier, seat_limit });
                    await this.loadWorkspaceDetail(wsId);
                    await this.loadWorkspaces();
                    this.render();
                    toast('success', 'Workspace updated');
                } catch (err) { toast('error', err.message); }
            });
        }

        // Add member to workspace
        const wsAddMemberBtn = this.el.querySelector('#admin-ws-add-member-btn');
        if (wsAddMemberBtn) {
            wsAddMemberBtn.addEventListener('click', async (e) => {
                const wsId = e.target.dataset.wsId;
                const email = this.el.querySelector('#admin-ws-add-email')?.value?.trim();
                const role = this.el.querySelector('#admin-ws-add-role')?.value || 'editor';
                if (!email) { toast('error', 'Email is required'); return; }
                try {
                    await client.adminAddWorkspaceMember(wsId, { email, role });
                    await this.loadWorkspaceDetail(wsId);
                    await this.loadWorkspaces();
                    this.render();
                    toast('success', `Added ${email} as ${role}`);
                } catch (err) { toast('error', err.message); }
            });
        }

        // Remove member from workspace
        this.el.querySelectorAll('.admin-ws-remove-member').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const wsId = e.target.dataset.wsId;
                const userId = e.target.dataset.userId;
                if (!confirm('Remove this member from the workspace?')) return;
                try {
                    await client.adminRemoveWorkspaceMember(wsId, userId);
                    await this.loadWorkspaceDetail(wsId);
                    await this.loadWorkspaces();
                    this.render();
                    toast('success', 'Member removed');
                } catch (err) { toast('error', err.message); }
            });
        });

        // Change member role in workspace
        this.el.querySelectorAll('.admin-ws-member-role').forEach(sel => {
            sel.addEventListener('change', async (e) => {
                const wsId = e.target.dataset.wsId;
                const userId = e.target.dataset.userId;
                const role = e.target.value;
                try {
                    await client.adminUpdateWorkspaceMemberRole(wsId, userId, { role });
                    toast('success', 'Role updated');
                } catch (err) { toast('error', err.message); }
            });
        });

        // Login As from workspace member
        this.el.querySelectorAll('.admin-ws-login-as').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const userId = e.target.dataset.userId;
                const member = this.expandedWorkspaceData?.members?.find(m => m.user_id === userId);
                if (!confirm(`Login as ${member?.email || 'this user'}? This will end your current session.`)) return;
                try { await client.adminImpersonate(userId); window.location.reload(); }
                catch (err) { toast('error', err.message); }
            });
        });

        // Save Agency Brain
        const wsSaveBrain = this.el.querySelector('#admin-ws-save-brain');
        if (wsSaveBrain) {
            wsSaveBrain.addEventListener('click', async (e) => {
                const wsId = e.target.dataset.wsId;
                const data = {};
                this.el.querySelectorAll('.admin-ws-brain-field').forEach(textarea => {
                    data[textarea.dataset.field] = textarea.value?.trim() || '';
                });
                try {
                    await client.adminUpdateAgencyBrain(wsId, data);
                    toast('success', 'Agency Brain saved');
                } catch (err) { toast('error', err.message); }
            });
        }
    }

    esc(str) { if (!str) return ''; const d = document.createElement('div'); d.textContent = str; return d.innerHTML; }
    unmount() {
        if (this._dashboardInterval) {
            clearInterval(this._dashboardInterval);
            this._dashboardInterval = null;
        }
        if (this.el) this.el.innerHTML = '';
    }
}
